import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  StickyNote,
  Stethoscope,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { mensajeDe } from '@/api/errors';
import { LogoCaso } from '@/components/MarcaCaso';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { answerFeedback, pressFeedback, swapCase } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { useAtribucionAlCopiar } from '@/lib/atribucion';

import {
  useCasosContenido,
  useNotas,
  useResponderCasoContenido,
  type ContentCases,
  type Letra,
  type TipoContenido,
} from '../curso/queries';

/**
 * Botones "Casos clínicos" y "Notas" de un contenido de la biblioteca. Los casos se califican en
 * la API (un intento por caso, como el legacy) y el cronómetro solo es informativo.
 */
export function ApoyosContenido({ tipo, id }: { tipo: TipoContenido; id: number }) {
  const [casos, setCasos] = useState(false);
  const notas = useNotas(tipo, id);
  const resumen = useCasosContenido(tipo, id);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {/* Verde como el «btn-case» del legacy. */}
        <button
          type="button"
          className="btn-cs btn-exito"
          disabled={!resumen.data || resumen.data.total === 0}
          onClick={() => {
            setCasos(true);
          }}
        >
          <Stethoscope size={18} /> Casos clínicos
          {resumen.data && resumen.data.total > 0 && (
            <span className="insignia ml-1">
              {resumen.data.answered}/{resumen.data.total}
            </span>
          )}
        </button>
      </div>
      {notas.data && notas.data.notes.length > 0 && (
        <aside className="superficie-lectura border-l-4 border-l-cian-300 p-5">
          <h2 className="flex items-center gap-2 font-extrabold text-azul-texto">
            <StickyNote size={18} className="text-cian-texto" /> Notas del autor
          </h2>
          <ul className="mt-2 space-y-2 text-sm leading-relaxed">
            {notas.data.notes.map((n) => (
              <li key={n.id} className="whitespace-pre-line">
                {n.text}
              </li>
            ))}
          </ul>
        </aside>
      )}
      <Dialog open={casos} onOpenChange={setCasos}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogTitle>Casos clínicos</DialogTitle>
          <DialogDescription>Elige una respuesta: tienes un intento por caso.</DialogDescription>
          {casos && <Casos tipo={tipo} id={id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Casos({ tipo, id }: { tipo: TipoContenido; id: number }) {
  const { data, error } = useCasosContenido(tipo, id);
  if (error) return <p className="text-rojo-texto">{mensajeDe(error)}</p>;
  if (!data) return <div className="h-64 animate-pulse rounded-xl bg-white/5" />;
  return <ListaCasos tipo={tipo} id={id} data={data} />;
}

function ListaCasos({ tipo, id, data }: { tipo: TipoContenido; id: number; data: ContentCases }) {
  useAtribucionAlCopiar();
  const responder = useResponderCasoContenido(tipo, id);
  // Abre en el primer caso sin contestar.
  const [i, setI] = useState(() =>
    Math.max(
      data.cases.findIndex((c) => !c.answered),
      0,
    ),
  );
  const [seleccion, setSeleccion] = useState<Letra | null>(null);
  const inicio = useRef(0);
  const tarjeta = useRef<HTMLDivElement>(null);
  const justificacion = useRef<HTMLDivElement>(null);

  // El cronómetro (informativo) arranca cada vez que se muestra un caso.
  useEffect(() => {
    inicio.current = Date.now();
  }, [i]);

  const caso = data.cases[i];
  if (!caso) return <p className="text-texto-2">No hay casos clínicos para este elemento.</p>;

  const ir = (n: number) => {
    const dir = n > i ? 1 : -1;
    swapCase(null, tarjeta.current, dir);
    setI(n);
    setSeleccion(null);
  };
  const estado = (l: Letra) =>
    !caso.answered
      ? seleccion === l
        ? ('seleccionada' as const)
        : undefined
      : l === caso.correctOption
        ? ('correcta' as const)
        : l === caso.selected
          ? ('incorrecta' as const)
          : undefined;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm text-texto-2">
        <span className="font-bold">
          Caso {i + 1} de {data.total}
        </span>
        <span>
          Contestados {data.answered} · Correctos {data.correct}
        </span>
      </div>
      <div ref={tarjeta} className="space-y-4">
        <p className="superficie-lectura max-h-60 overflow-y-auto p-4 leading-relaxed">
          <LogoCaso className="mr-2 align-[-2px]" />
          {caso.vignette}
        </p>
        <p className="barra-pregunta">
          <LogoCaso variante="blanco" className="shrink-0" />
          {caso.question}
        </p>
        <div role="radiogroup" className="space-y-2">
          {caso.options.map((o) => (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={(caso.selected ?? seleccion) === o.key}
              data-opcion={o.key}
              data-estado={estado(o.key)}
              disabled={caso.answered || responder.isPending}
              className="opcion w-full text-left disabled:cursor-default"
              onClick={(e) => {
                pressFeedback(e.currentTarget);
                setSeleccion(o.key);
              }}
            >
              <span className="opcion__letra flex flex-col gap-0.5">
                {o.key}
                <LogoCaso variante="blanco" className="h-2" />
              </span>
              <span className="opcion__texto normal-case">{o.text}</span>
            </button>
          ))}
        </div>
        {caso.answered && (
          <div ref={justificacion} className="superficie-lectura space-y-2 p-4">
            <p
              className={cn(
                'flex items-center gap-2 font-extrabold',
                caso.isCorrect ? 'text-verde-texto' : 'text-rojo-texto',
              )}
            >
              {caso.isCorrect ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
              {caso.isCorrect ? '¡Correcto!' : 'Incorrecto'}
            </p>
            <p className="text-sm leading-relaxed">{caso.justification}</p>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="btn-cs btn-vidrio"
          disabled={i === 0}
          onClick={() => {
            ir(i - 1);
          }}
        >
          <ChevronLeft size={16} /> Anterior
        </button>
        {!caso.answered ? (
          <button
            type="button"
            className="btn-cs btn-exito"
            disabled={!seleccion || responder.isPending}
            onClick={() => {
              if (!seleccion) return;
              const seconds = Math.round((Date.now() - inicio.current) / 1000);
              responder.mutate(
                { caseId: caso.caseId, option: seleccion, seconds },
                {
                  onSuccess: (r) => {
                    requestAnimationFrame(() => {
                      answerFeedback(
                        tarjeta.current?.querySelector<HTMLElement>(
                          `[data-opcion="${seleccion}"]`,
                        ) ?? null,
                        { correct: !!r.isCorrect, explanation: justificacion.current },
                      );
                    });
                  },
                  onError: (e) => toast.error(mensajeDe(e)),
                },
              );
            }}
          >
            Responder
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          className="btn-cs btn-primario"
          disabled={i === data.cases.length - 1}
          onClick={() => {
            ir(i + 1);
          }}
        >
          Siguiente <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
